// The client half of D-301: a list ORDER over a field §7 withholds is not
// asked for by a reader who is not sent that field. Pure, unit-tested.
//
// The server is the boundary. `GET /v1/quotes` and `GET /v1/orders` refuse
// `order=±total_minor` with 403 PERMISSION_DENIED
// (`missing_permission: sales.read_value`) to a caller without the grant,
// because the cursor's anchor is the last row's sort value and an open money
// sort handed every total back one page at a time. This file keeps the
// screens from asking for what the server will refuse, in three places.
//
//   1. URL VALIDATION. A money order that arrives in the address (a manager's
//      shared link, a pasted URL, a stored view once D-243's saved views
//      land) is dropped by `validateSalesQuotesSearch` /
//      `validateSalesOrdersSearch` before any request is built, when the
//      reader is KNOWN not to hold the grant. "Known" is narrow on purpose:
//      the role mirror answers only for system roles, so a reader holding any
//      custom role is "unknown" and the request goes to the server, which
//      decides. A custom role that holds the grant must keep its sort.
//      Since D-301 round 7 the screen also takes the token out of the address
//      (`addressCarriesRefusedValueOrder`), so a known non-holder and a
//      server refusal leave the same address. A token left there would stay
//      through every reload and in every link the reader copies.
//   2. THE 403 FALLBACK. When the server refuses the order anyway (a custom
//      role without the grant), the screen drops the order from the address
//      and asks again, instead of showing an error card for a sort the
//      reader never needed.
//   3. THE CARET. The money column, and with it the caret, renders only when
//      a row carries the key (D-239 ruling 2, `columnPresent`). The payload
//      decides and the mirror does not: `total_minor` is non-nullable, so a
//      holder's rows always carry it, and a custom role that holds the grant
//      keeps the caret its rows prove.
//
// ROUND 3 (D-301): the same verdict and the same refusal check serve the two
// reads whose whole answer is prices. `GET /v1/price-lists/{id}` and
// `POST /v1/pricing/resolve` refuse a caller without the grant with the same
// problem. The §4.2 price check does not offer its form to a reader the
// mirror KNOWS is refused, and both it and the §4.3 detail screen answer the
// server's refusal with a stated sentence, never an error card.

import { RequestError } from "../../../../lib/request";
import { rolesAreKnown, type RoleSignal } from "../../../../lib/role-codes";
import { SALES_ROLE_GRANTS } from "../hooks/permissions";

/** The §7 field grant for a sales document's money. */
export const SALES_READ_VALUE = "sales.read_value";

/** The list-order KEYS over a field the payload omits without
 * `sales.read_value` — the same set the API fences (value-fence.ts): the
 * books' `total_minor`, and since P3-E2 PR E2-1 the deal list's
 * `expected_value_minor` (spec 09 §4.14, D-312 ruling 12). */
export const VALUE_ORDER_KEYS: ReadonlySet<string> = new Set([
  "total_minor",
  "expected_value_minor",
]);

/** Is this D-242 order token (`key` or `-key`) a money order? */
export function isValueOrderToken(token: string): boolean {
  return VALUE_ORDER_KEYS.has(token.startsWith("-") ? token.slice(1) : token);
}

/**
 * What the client can say about this reader and the money sort.
 *   * `pending` — the role feed has not answered yet; a request carrying a
 *     money order waits for it rather than guessing.
 *   * `allowed` — the role mirror grants `sales.read_value`.
 *   * `refused` — every role the reader holds is a system role the mirror
 *     knows, and none of them grants the value. Only this verdict drops a
 *     money order at URL validation.
 *   * `unknown` — the feed failed, or the reader holds a role the mirror
 *     cannot see into (a tenant's custom role). The server decides, and the
 *     403 fallback covers a refusal.
 */
export type ValueSortVerdict = "pending" | "allowed" | "refused" | "unknown";

export interface ValueSortReader extends RoleSignal {
  readonly isResolved: boolean;
  readonly isFeedError: boolean;
  readonly has: (code: string) => boolean;
}

export function valueSortVerdict(reader: ValueSortReader): ValueSortVerdict {
  if (!reader.isResolved) return "pending";
  if (reader.isFeedError) return "unknown";
  if (reader.has(SALES_READ_VALUE)) return "allowed";
  // Since D-586 the feed says what a custom role holds, so only a feed row
  // without the permission list leaves the answer to the server.
  if (!rolesAreKnown(reader, SALES_ROLE_GRANTS)) return "unknown";
  return "refused";
}

/** Is this error the server refusing a VALUE read to a reader without the
 * grant (D-301)? A money list order, a price list's items and the pricing dry
 * run all refuse with the same problem. Read off the problem's own
 * `missing_permission`, never off the status alone: a 403 for any other
 * reason is a real refusal and keeps its error card. */
export function isSalesValueRefusal(error: unknown): boolean {
  if (!(error instanceof RequestError)) return false;
  if (error.httpStatus !== 403 || error.code !== "PERMISSION_DENIED") return false;
  const problem = error.problem as Record<string, unknown> | undefined;
  return problem?.["missing_permission"] === SALES_READ_VALUE;
}

/** The list screens' name for the same check: a refused money ORDER. */
export const isValueOrderRefusal = isSalesValueRefusal;

/** Does the ADDRESS still carry a money order that URL validation dropped for
 * this reader? True only for a `refused` verdict, the one verdict that drops
 * the token without asking the server. The screen then rewrites the address
 * without it, exactly as it does after the server's 403 (D-301 round 7). */
export function addressCarriesRefusedValueOrder(
  search: Readonly<Record<string, unknown>>,
  verdict: ValueSortVerdict,
): boolean {
  const order = search["order"];
  return verdict === "refused" && typeof order === "string" && isValueOrderToken(order);
}
